import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs';
import { DateHelper } from '../../helpers/date.helper';
import { FormatType } from '../../enums/format-type.enum';
import { FormHelper } from '../../helpers/form.helper';
import { DEFAULT_DEBOUNCE_TIME } from '../../types/constants';

@Component({
  selector: 'shared-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.scss'],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: TimePickerComponent, multi: true },
    { provide: NG_VALIDATORS, multi: true, useExisting: TimePickerComponent },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TimePickerComponent implements ControlValueAccessor, Validator {
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() required = true;
  @Input() set isTime24h(value: boolean) {
    this._isTime24H = value;
    this.updateTimeFormat();
  }

  get isTime24h(): boolean {
    return this._isTime24H;
  }

  private _isTime24H = DateHelper.is24HFormat;

  @ViewChild('amPmToggle', { static: false }) amPmToggleElement: ElementRef<HTMLElement>;

  time: number;
  disabled: boolean;
  timePickerForm = this.formBuilder.group({
    hour: new FormControl('', [Validators.min(0), this.isTime24h ? Validators.max(23) : Validators.max(12)]),
    minute: new FormControl('', [Validators.min(0), Validators.max(59)]),
    dayTimePeriod: new FormControl({ value: 'AM', disabled: this.isTime24h }),
  },
  {
    validators: [this.hourAndMinuteRequiredValidator.bind(this)],
  });

  readonly maxDayTime = 86334000;
  readonly timeInterval = 600000;
  readonly FormatType = FormatType;

  private onChange = (time: number): void => {};
  private onTouched = (): void => {};
  onValidatorChange: any = () => {};

  get f() {
    return this.timePickerForm.controls;
  }

  get isInvalid(): boolean {
    return this.timePickerForm.invalid && (this.timePickerForm.touched || this.timePickerForm.dirty);
  }

  constructor() {
    this.timePickerForm.valueChanges.pipe(debounceTime(DEFAULT_DEBOUNCE_TIME)).subscribe(value => {
      if (value.hour && value.minute) {
        this.decomposeTimeFromForm(value as TimePickerType);
      }
    });
  }

  private updateTimeFormat(): void {
    this.writeValue(this.time);
  }

  selectInput(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  onBlur(event: Event, type: 'hour' | 'minute'): void {
    const input = event.target as HTMLInputElement;
    input.value = this.padDigit(input.value);
    if (input.value === '00' && !this.isTime24h && type === 'hour') {
      input.value = '12';
    }
  }

  onInput(event: Event, type: 'hour' | 'minute'): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    const isNumber = /^\d+$/.test(value);
    if (!isNumber) {
      input.value = value.replace(/\D/g, '');
    }

    if (type === 'hour') {
      const hour = +input.value;
      if (input.value.length === 1 && ((this.isTime24h && hour > 2) || (!this.isTime24h && hour > 1))) {
        input.value = '0' + input.value;
      } else if (this.isTime24h && hour > 23) {
        input.value = '23';
      } else if (!this.isTime24h && (input.value === '00' || hour > 12)) {
        const hour12 = hour === 0 ? 12 : hour % 12;
        this.timePickerForm.patchValue({ hour: this.padDigit(hour12), minute: this.f.minute.value, dayTimePeriod: hour < 12 ? 'AM' : 'PM' }, { emitEvent: false });
      }
    } else {
      const minute = +input.value;
      if (minute >= 59) {
        this.f.minute.patchValue('59', { emitEvent: false });
      }
    }

    if (input.value.length >= 2) {
      this.focusNextInputSibling(input);
    }
  }

  private focusNextInputSibling(input: HTMLInputElement): void {
    if (input.name === 'minute' && this.amPmToggleElement) {
      this.amPmToggleElement.nativeElement.focus();
      return;
    }
    const nextInput = input.nextElementSibling as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
    }
  }

  addTime(): void {
    this.addTimeInterval(this.timeInterval);
  }

  subTime(): void {
    this.addTimeInterval(-this.timeInterval);
  }

  private addTimeInterval(n: number): void {
    if (!this.time) {
      this.time = 0;
    }

    this.decomposeTimeFromForm(this.timePickerForm.value as TimePickerType);
    const time = Math.round(this.time / n) * n + n;
    this.updateTime(time);
  }

  toggleAmPm(): void {
    if (this.f.dayTimePeriod?.disabled) {
      return;
    }
    const currentFormat = this.f.dayTimePeriod?.value;
    const newFormat = currentFormat === 'AM' ? 'PM' : 'AM';
    this.setTimeFormat(newFormat);
  }

  private setTimeFormat(format: 'AM' | 'PM'): void {
    if (this.timePickerForm.invalid) {
      return;
    }
    this.f.dayTimePeriod?.patchValue(format, { emitEvent: false });
    this.timePickerForm.updateValueAndValidity();
  }

  private updateTime(time: number): void {
    if (time < 0) {
      time = 0;
    } else if (time > this.maxDayTime) {
      time = this.maxDayTime;
    }
    this.time = time;
    this.writeValue(time);
    this.notifyChanged();
  }

  private decomposeTimeFromForm(value: TimePickerType): void {
    const hours = this.padDigit(value.hour);
    const minutes = this.padDigit(value.minute);
    const dateTimePeriod = value.dayTimePeriod ?? this.f.dayTimePeriod?.value;
    this.time = this.isTime24h ? DateHelper.parseDuration(`${hours}:${minutes}`) : DateHelper.parse12HTime(`${hours}:${minutes}:${dateTimePeriod}`);
    this.notifyChanged();
    this.cdr.markForCheck();
  }

  private notifyChanged(): void {
    if (this.onChange) {
      this.onChange(this.time);
    }
    if (this.onTouched) {
      this.onTouched();
    }
  }

  private padDigit(value: string | number): string {
    return value.toString().padStart(2, '0');
  }

  writeValue(time: number): void {
    if (time == null) {
      this.timePickerForm.reset({ hour: '', minute: '', dayTimePeriod: 'AM' }, { emitEvent: false });
      return;
    }
    this.time = Number(time);
    const splitTime = this.isTime24h ? DateHelper.format24HTime(this.time).split(':') : DateHelper.format12HTime(this.time).split(':');
    const timeFormat = splitTime.length > 2 ? (splitTime[2] as 'AM' | 'PM') : 'AM';
    this.f.hour.patchValue(splitTime[0] ?? '00', { emitEvent: false });
    this.f.minute.patchValue(splitTime[1] ?? '00', { emitEvent: false });
    this.f.dayTimePeriod.patchValue(timeFormat, { emitEvent: false });
    FormHelper.setEnabled(this.f.dayTimePeriod, !this.isTime24h, false);
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (time: number) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;

    FormHelper.setEnabled(this.f.hour, !isDisabled, false);
    FormHelper.setEnabled(this.f.minute, !isDisabled, false);
    this.cdr.markForCheck();
  }

  registerOnValidatorChange(onValidatorChange: () => void): void {
    this.onValidatorChange = onValidatorChange;
  }

  private hourAndMinuteRequiredValidator(control: AbstractControl): ValidationErrors {
    const f = control as typeof this.timePickerForm;
    if (f.value.hour == null && f.value.minute != null) {
      f.controls.hour.setErrors({ required: true });
      return { hourRequired: true };
    } else if (f.value.hour != null && f.value.minute == null) {
      f.controls.minute.setErrors({ required: true });
      return { minuteRequired: true };
    }
    return null;
  }

  validate(control: AbstractControl): ValidationErrors {
    const errors: ValidationErrors = {};

    if (this.f.hour.errors) {
      errors.hour = this.f.hour.errors;
    }
    if (this.f.minute.errors) {
      errors.minute = this.f.minute.errors;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }
}

export interface TimePickerType {
  hour: string;
  minute: string;
  dayTimePeriod?: 'AM' | 'PM';
}
